import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpenText, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

export default function GalleryPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-sky-700"
        >
          Information Gallery
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          Everything OTET, in one shelf.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          Exam patterns, rules and study digests — open any card to read the full note.
        </motion.p>

        {items === null ? (
          <div className="mt-16 flex items-center gap-3 text-slate-500" data-testid="gallery-loading">
            <Loader2 size={18} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="mt-16 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="gallery-empty">
            Information cards will appear here once added.
          </p>
        ) : (
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.08, duration: 0.7, ease: EASE }}
                data-testid={`gallery-card-${i}`}
                className="group flex flex-col rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-colors duration-200 hover:border-sky-300"
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-sky-100 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-sky-800">
                    {item.category}
                  </span>
                  <BookOpenText size={18} className="text-slate-300 transition-colors duration-200 group-hover:text-sky-600" />
                </div>
                <h2 className="mt-5 font-display text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
                  {item.title}
                </h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{item.summary}</p>
                <Link
                  to={`/info/${item.id}`}
                  data-testid={`gallery-open-${i}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-sky-700 transition-colors duration-200 hover:text-sky-900"
                >
                  Read the full note
                  <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
